import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Home, Heart, Calendar, Footprints, Trophy, Store, Star, Mail, PawPrint, Route } from 'lucide-react';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Scroll to top when navigating to a new page
  useEffect(() => {
    if (!isHome) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [location.pathname]);
  // When landing on home with a hash (e.g. /#recorrido), scroll to that section
  useEffect(() => {
    if (isHome && location.hash) {
      // Wait for sections to mount before scrolling
      const id = location.hash.slice(1);
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
        }
      };
      // Small delay to allow page render
      const timeout = setTimeout(tryScroll, 100);
      return () => clearTimeout(timeout);
    }
  }, [isHome, location.hash, location.pathname]);
  const navLinks = [{
    name: 'Inicio',
    href: '#inicio',
    type: 'scroll' as const,
    icon: Home
  }, {
    name: 'El Movimiento',
    href: '#movimiento',
    type: 'scroll' as const,
    icon: Heart
  }, {
    name: 'Agenda',
    href: '#agenda',
    type: 'scroll' as const,
    icon: Calendar
  }, {
    name: 'El Recorrido',
    href: '#recorrido',
    type: 'scroll' as const,
    icon: Route
  }, {
    name: 'Muro de Huellas',
    href: '#muro',
    type: 'scroll' as const,
    icon: PawPrint
  }, {
    name: 'Registro Mascotas / Caminata',
    href: '/caminata-canina',
    type: 'route' as const,
    icon: Footprints
  }, {
    name: 'Deportes',
    href: '/deportes',
    type: 'route' as const,
    icon: Trophy
  }, {
    name: 'Expositores',
    href: '/expositores',
    type: 'route' as const,
    icon: Store
  }, {
    name: 'Patrocinadores',
    href: '/patrocinadores',
    type: 'route' as const,
    icon: Star
  }, {
    name: 'Contacto',
    href: '#contacto',
    type: 'scroll' as const,
    icon: Mail
  }];
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, link: (typeof navLinks)[0]) => {
    setIsMobileMenuOpen(false);
    if (link.type === 'scroll') {
      e.preventDefault();
      if (isHome) {
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      } else {
        navigate('/' + link.href);
      }
    }
    // For 'route' type, let the Link component handle it naturally
  };
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || !isHome ? 'bg-brand-navy py-3 shadow-[0_4px_12px_rgba(0,0,0,0.25)]' : 'bg-transparent py-5'}`} data-id="element-602" data-mp="16goxrutg1fc">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-603" data-mp="sxml5x7f1ezc">
        <div className="flex justify-between items-center" data-id="element-604" data-mp="xtv65r8ao2ef">
          {/* Logo */}
          <Link to="/" onClick={e => {
          if (isHome) {
            e.preventDefault();
            const hero = document.getElementById('inicio');
            if (hero) {
              hero.scrollIntoView({
                behavior: 'smooth'
              });
            } else {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }
          }
          setIsMobileMenuOpen(false);
        }} className="flex-shrink-0 z-50 cursor-pointer" data-id="element-605" data-mp="k1wzdn222ilf">
            <img src="https://cdn.magicpatterns.com/uploads/jwd1cAyWsGKKne9ojQUmio/Logo_latido_y_huella_ICONO_blanco.png" alt="Latido & Huella — Ir al inicio" className="h-10 md:h-12 object-contain" data-id="element-606" data-mp="3f5u054tzn98g" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6" data-id="element-607" data-mp="6qaasgwfuayo">
            <ul className="flex space-x-4" data-id="element-608" data-mp="1vwl8v4giy1x">
              {navLinks.map(link => {
              const isActive = link.type === 'route' && location.pathname === link.href;
              const Icon = link.icon;
              return <li key={link.name} className="relative group" data-id="element-609" data-mp="pma6o2sjdynj">
                    {link.type === 'route' ? <Link to={link.href} className={`p-2.5 rounded-full flex items-center justify-center transition-all duration-300 ${isActive ? 'bg-brand-cyan text-white' : 'text-white/80 hover:bg-white/10 hover:text-brand-cyan'}`} data-id="element-610" data-mp="28ablmbav0v77">
                        <Icon size={24} strokeWidth={2.2} data-id="element-611" data-mp="2mae685ajy2q5" />
                      </Link> : <a href={link.href} onClick={e => handleNavClick(e, link)} className="p-2.5 rounded-full flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-brand-cyan transition-all duration-300" data-id="element-612" data-mp="2r7wedtgqwdx3">
                        <Icon size={24} strokeWidth={2.2} data-id="element-613" data-mp="351nde7wuofek" />
                      </a>}
                    {/* Tooltip */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-white text-brand-navy text-xs font-bold rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 shadow-lg whitespace-nowrap pointer-events-none" data-id="element-614" data-mp="3ex78is3lba29">
                      {link.name}
                      {/* Tooltip arrow */}
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45" data-id="element-615" data-mp="1juu252lfwwyc"></div>
                    </div>
                  </li>;
            })}
            </ul>
            <Link to="/caminata-canina" className="bg-brand-cyan hover:bg-blue-700 text-white px-6 py-2.5 rounded-full font-bold transition-colors shadow-md ml-2" data-id="element-616" data-mp="1tkx1rln2h95n">
              Inscríbete
            </Link>
            <a href="https://admin-latidoyhuella.netlify.app/admin" target="_blank" rel="noopener noreferrer" className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-3 py-2 rounded-full font-semibold transition-colors shadow-sm" title="Panel administrativo" data-id="element-617" data-mp="22lux7fjfw8cm">
              Panel
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center z-50" data-id="element-618" data-mp="2a547dazrsb2q">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-brand-cyan transition-colors focus:outline-none" data-id="element-619" data-mp="2a4kq7why1qpr">
              {isMobileMenuOpen ? <X size={28} data-id="element-620" data-mp="216ir5mhrt854" /> : <Menu size={28} data-id="element-621" data-mp="262zhmncfzyld" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`fixed inset-0 bg-brand-navy z-40 flex flex-col justify-center items-center transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'} lg:hidden`} data-id="element-622" data-mp="1r6rtxd1tsf1c">
        <ul className="flex flex-col items-center space-y-6 w-full px-6" data-id="element-623" data-mp="3lksuc2d0xa3j">
          {navLinks.map(link => <li key={link.name} className="w-full text-center" data-id="element-624" data-mp="3qh011xd8upmj">
              {link.type === 'route' ? <Link to={link.href} onClick={() => setIsMobileMenuOpen(false)} className={`block text-xl font-semibold transition-colors ${location.pathname === link.href ? 'text-brand-cyan' : 'text-white hover:text-brand-cyan'}`} data-id="element-625" data-mp="2ebk1waykkab7">
                  {link.name}
                </Link> : <a href={link.href} onClick={e => handleNavClick(e, link)} className="block text-white text-xl font-semibold hover:text-brand-cyan transition-colors" data-id="element-626" data-mp="1un2y0ccn3523">
                  {link.name}
                </a>}
            </li>)}
          <li className="w-full pt-6" data-id="element-627" data-mp="3fz2x9tphgtxc">
            <Link to="/caminata-canina" onClick={() => setIsMobileMenuOpen(false)} className="block w-full text-center bg-brand-cyan hover:bg-blue-700 text-white px-6 py-4 rounded-full font-bold text-lg transition-colors shadow-md" data-id="element-628" data-mp="3fy1l1lwqe1e1">
              Inscríbete
            </Link>
          </li>
          <li className="w-full pt-2" data-id="element-629" data-mp="3ng9bkohzc9at">
            <a href="https://admin-latidoyhuella.netlify.app/admin" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)} className="block w-full text-center bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-full font-semibold text-sm transition-colors shadow-sm" data-id="element-630" data-mp="3s92f591ppblb">
              Panel
            </a>
          </li>
        </ul>
      </div>
    </nav>;
}